import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Branch } from "@dentacore/shared-types";

import { listBranches, updateBranch } from "../api/branches";
import { activeBranch } from "../api/tokens";
import { useActiveBranch } from "../api/use-active-branch";
import { refreshBranchCurrencies } from "../api/use-branch-currency";
import { BranchForm } from "./branch-form";
import { PaneHead } from "./pane";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";
import { formatPhone } from "../ui/countries";

/**
 * One location: what it is, how to reach it, and closing it.
 *
 * ── What left this page, and why ──────────────────────────────────────────
 *
 * It used to hold this location's opening hours, chairs, block reasons **and**
 * its whole procedure catalogue — 485 lines, five reads on mount, and the only
 * route to five of the nine things an admin configures. That was the reason
 * Settings was called unintuitive: nothing on the index named those five, so
 * finding them meant already knowing that prices belong to a branch.
 *
 * They are now their own panes, scoped by the rail's switcher
 * (`location-panes.tsx`). What stays here is what belongs to the *location
 * itself* rather than to the work done there: its timezone, how to reach it,
 * and whether it is open. **Turnover left this pane on 2026-09-23** — it is a
 * chair's clean-down time, not a location's, and is set in the calendar's
 * *Edit chairs* dialog.
 *
 * ── Arriving here still selects the branch ────────────────────────────────
 *
 * The chairs and catalogue endpoints are scoped by a request header taken from
 * one active branch id, so opening a location has always had to set it. That is
 * unchanged — but it is no longer *hidden*, because the rail's switcher shows
 * the same value and says out loud that it also moves the calendar.
 *
 * **A branch the caller is not rostered to still shows its details** — a name
 * and a timezone are clinic-scoped and editable by any admin (decision S7).
 */
export function BranchPage(): React.JSX.Element {
  const { branchId } = useParams<{ branchId: string }>();
  const active = useActiveBranch();
  const { busy, toast, dismiss, fail, run } = useWriter();

  const [branch, setBranch] = useState<Branch | null | "missing">(null);
  const [editing, setEditing] = useState(false);
  /** Whether the "close this location?" confirmation is showing. */
  const [confirmingClose, setConfirmingClose] = useState(false);

  const reloadBranch = useCallback(
    async (signal?: AbortSignal) => {
      const rows = await listBranches(signal);
      setBranch(rows.find((row) => row.id === branchId) ?? "missing");
    },
    [branchId],
  );

  // Arriving here is what selects the branch — see the note above.
  useEffect(() => {
    if (branchId !== undefined && branchId !== active) activeBranch.set(branchId);
  }, [branchId, active]);

  useEffect(() => {
    const controller = new AbortController();

    reloadBranch(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setBranch("missing");
      fail(describe(cause, "Could not read this location."));
    });

    return () => controller.abort();
  }, [reloadBranch, fail]);

  if (branch === "missing") {
    return (
      <>
        <BackLink />
        <p className={styles.blank}>That location is not in this clinic.</p>
      </>
    );
  }

  if (branch === null) {
    return (
      <>
        <BackLink />
        <p className={styles.quiet}>Reading&hellip;</p>
      </>
    );
  }

  return (
    <>
      <BackLink />

      <PaneHead title={branch.name} testId="branch-title" />

      {!branch.isActive && (
        <p className={styles.notice} data-testid="branch-closed">
          This location is closed. It takes no new bookings, does not appear on the calendar, and
          refuses every change until it is reopened &mdash; use <b>Reopen</b> at the foot of this
          page. Everything it already recorded stays readable.
        </p>
      )}

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>Where and when</h3>
          </div>
          {!editing && (
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => setEditing(true)}
              data-testid="branch-edit"
            >
              Edit details
            </button>
          )}
        </div>

        {editing ? (
          <div className={styles.cardBody}>
            <BranchForm
              branch={branch}
              busy={busy}
              defaultTimezone={branch.timezone}
              onCancel={() => setEditing(false)}
              onSubmit={(body) => {
                void run(() => updateBranch(branch.id, body), reloadBranch, "Saved.").then(
                  (saved) => {
                    // Closed on the write landing and on nothing else — the
                    // same correction every other form here took on 2026-09-06.
                    if (saved) setEditing(false);
                    // A new currency changes how every figure here is read.
                    if (saved && body.currency !== undefined) refreshBranchCurrencies();
                  },
                );
              }}
            />
          </div>
        ) : (
          <dl className={styles.facts}>
            <Fact label="Timezone" value={branch.timezone} />
            <Fact label="Currency" value={branch.currency} />
            <Fact label="Phone" value={formatPhone(branch.phoneE164) || "Not set"} />
            <Fact
              label="Address"
              value={branch.address === "" ? "Not set" : (branch.address ?? "Not set")}
            />
          </dl>
        )}
      </section>

      {/*
        ── the danger zone ──────────────────────────────────────────────────
        **Closing a location used to be one click in the page header**, beside
        "Edit details", and it is the most destructive control on these screens:
        a closed branch takes no bookings, leaves the calendar, and — until the
        guard was corrected the same day — made every branch-scoped request
        refuse with a message about membership that was simply false. The client
        lost an afternoon to it.

        It gained a confirmation on 2026-09-06; what it did not gain was a
        position that made reaching it deliberate. `Reopen` lives here too,
        because splitting the pair across two ends of the page is how somebody
        fails to find the way back.
      */}
      <section className={`${styles.card} ${styles.cardDanger}`} data-testid="branch-danger">
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>{branch.isActive ? "Danger zone" : "Closed"}</h3>
          </div>
          {branch.isActive ? (
            <button
              type="button"
              className={styles.danger}
              disabled={busy || confirmingClose}
              onClick={() => setConfirmingClose(true)}
              data-testid="branch-close"
            >
              Close location
            </button>
          ) : (
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => {
                void run(
                  () => updateBranch(branch.id, { isActive: true }),
                  reloadBranch,
                  "Location reopened.",
                );
              }}
              data-testid="branch-reopen"
            >
              Reopen
            </button>
          )}
        </div>

        {confirmingClose && (
          <div className={styles.cardBody} role="alert" data-testid="branch-close-confirm">
            <p className={styles.confirmText}>
              Close {branch.name}? It stops taking bookings and disappears from the calendar and the
              branch switcher, and nobody can add chairs, procedures or appointments to it until it
              is reopened. Nothing is deleted, and this page can reopen it.
            </p>
            <span className={styles.confirmActions}>
              <button
                type="button"
                className={styles.danger}
                disabled={busy}
                onClick={() => {
                  setConfirmingClose(false);
                  void run(
                    () => updateBranch(branch.id, { isActive: false }),
                    reloadBranch,
                    "Location closed.",
                  );
                }}
                data-testid="branch-close-yes"
              >
                Close it
              </button>
              <button
                type="button"
                className={styles.ghost}
                disabled={busy}
                onClick={() => setConfirmingClose(false)}
                data-testid="branch-close-cancel"
              >
                Keep it open
              </button>
            </span>
          </div>
        )}
      </section>

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}

function BackLink(): React.JSX.Element {
  return (
    <Link to="/settings/locations" className={styles.back} data-testid="back-to-settings">
      <span aria-hidden="true">‹</span> Locations
    </Link>
  );
}

function Fact({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className={styles.fact}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}
